import { useEffect, useRef, useState } from 'react';
import {
    createChart,
    IChartApi,
    ISeriesApi,
    UTCTimestamp,
    CandlestickData,
} from 'lightweight-charts';


import { useRealTimeStockPrice, fetchHistoricalCandles } from '../services/RealTimeStockService';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Stock, Order } from '../types';
import { MockTradingAPI } from '../services/mockAPI';
import { Modal } from '../components/atoms/Modal';
import { OrderForm } from '../components/molecules/OrderForm';
import { Card } from '../components/atoms/Card';
import { Select } from '../components/atoms/Select';
import { Button } from '../components/atoms/Button';


const RealTimeDashboardPage: React.FC = () => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);

    const [selectedStock, setSelectedStock] = useState<string>('AAPL');
    const [timeframe, setTimeframe] = useState<'1m' | '5m' | '15m' | '1h' | '1d'>('1d');
    const [orderStock, setOrderStock] = useState<Stock | null>(null);
    const [orderType, setOrderType] = useState<'buy' | 'sell' | null>(null);

    const priceData = useRealTimeStockPrice(selectedStock);
    const queryClient = useQueryClient();

    const stockOptions = [
        { value: 'AAPL', label: 'Apple Inc.' },
        { value: 'GOOGL', label: 'Alphabet Inc.' },
        { value: 'MSFT', label: 'Microsoft Corp.' },
        { value: 'TSLA', label: 'Tesla Inc.' },
        { value: 'NVDA', label: 'NVIDIA Corp.' },
    ];

    const orderMutation = useMutation<Order, Error, Omit<Order, 'id' | 'status' | 'timestamp'>>({
        mutationFn: MockTradingAPI.placeOrder,
        onSuccess: (order) => {
            const orders = JSON.parse(localStorage.getItem('mock-orders') || '[]') as Order[];
            orders.push(order);
            localStorage.setItem('mock-orders', JSON.stringify(orders));
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            setOrderStock(null);
            setOrderType(null);
        },
    });

    // Initialize chart
    useEffect(() => {
        if (!chartContainerRef.current) return;

        chartRef.current = createChart(chartContainerRef.current, {
            width: chartContainerRef.current.clientWidth,
            height: 400,
            layout: { background: { color: '#ffffff' }, textColor: '#333' },
            grid: { vertLines: { color: '#f0f0f0' }, horzLines: { color: '#f0f0f0' } },
            timeScale: { timeVisible: true, secondsVisible: timeframe === '1m' },
        });

        candlestickSeriesRef.current = chartRef.current.addCandlestickSeries({
            upColor: '#26a69a',
            downColor: '#ef5350',
            borderUpColor: '#26a69a',
            borderDownColor: '#ef5350',
            wickUpColor: '#26a69a',
            wickDownColor: '#ef5350',
        });

        const handleResize = () => {
            if (chartRef.current && chartContainerRef.current) {
                chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
            }
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            chartRef.current?.remove();
            chartRef.current = null;
        };
    }, [timeframe]);

    // Load historical candles
    useEffect(() => {
        const loadCandles = async () => {
            const candles = await fetchHistoricalCandles(selectedStock, timeframe);

            if (candlestickSeriesRef.current) {
                candlestickSeriesRef.current.setData(
                    candles.map((candle) => ({
                        time: Math.floor(new Date(candle.time).getTime() / 1000) as UTCTimestamp,
                        open: candle.open,
                        high: candle.high,
                        low: candle.low,
                        close: candle.close,
                    })) as CandlestickData[]
                );
            }
        };
        loadCandles();
    }, [selectedStock, timeframe]);

    // Update with real-time price
    useEffect(() => {
        if (priceData && candlestickSeriesRef.current) {
            candlestickSeriesRef.current.update({
                time: Math.floor(Date.now() / 1000) as UTCTimestamp,
                open: priceData.price,
                high: priceData.price,
                low: priceData.price,
                close: priceData.price,
            });
        }
    }, [priceData]);

    const handleOrder = (type: 'buy' | 'sell') => {
        if (!priceData) return;
        setOrderStock({
            symbol: selectedStock,
            name: stockOptions.find((opt) => opt.value === selectedStock)?.label || selectedStock,
            price: priceData.price,
            change: 0,
            changePercent: 0,
        });
        setOrderType(type);
    };

    const submitOrder = (orderData: Omit<Order, 'id' | 'status' | 'timestamp'>) => {
        orderMutation.mutate(orderData);
    };

    return (
        <div data-testid="realtime-page">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Real-Time Dashboard</h1>
            <Card className="p-6 mb-6">
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                    <Select
                        label="Select Stock"
                        options={stockOptions}
                        value={selectedStock}
                        onChange={(e) => setSelectedStock(e.target.value)}
                        data-testid="stock-selector"
                    />
                    <Select
                        label="Timeframe"
                        options={[
                            { value: '1m', label: '1 Minute' },
                            { value: '5m', label: '5 Minutes' },
                            { value: '15m', label: '15 Minutes' },
                            { value: '1h', label: '1 Hour' },
                            { value: '1d', label: '1 Day' },
                        ]}
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value as any)}
                        data-testid="timeframe-selector"
                    />
                </div>
                <div className="flex items-center gap-4">
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {selectedStock}: ${priceData?.price?.toFixed(2) || 'Loading...'}
                    </p>
                    <Button variant="success" size="sm" onClick={() => handleOrder('buy')} data-testid="buy-button">
                        Buy
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleOrder('sell')} data-testid="sell-button">
                        Sell
                    </Button>
                </div>
            </Card>
            <Card className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Candlestick Chart</h2>
                <div ref={chartContainerRef} className="w-full" data-testid="candlestick-chart" />
            </Card>
            <Modal
                isOpen={!!orderStock}
                onClose={() => {
                    setOrderStock(null);
                    setOrderType(null);
                }}
                title="Place Order"
            >
                {orderStock && (
                    <OrderForm
                        stock={orderStock}
                        type={orderType!}
                        onSubmit={submitOrder}
                        onCancel={() => {
                            setOrderStock(null);
                            setOrderType(null);
                        }}
                    />
                )}
            </Modal>
        </div>
    );
};

export default RealTimeDashboardPage;